import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  subscribeToOrderingStatus,
  isOrderingPausedFor,
  getPauseMessageFor,
  ORDERING_LOCATIONS
} from '../services/orderingStatusService';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [currentLocation, setCurrentLocation] = useState('Angola'); // Fallback for display only
  // Whether the customer has explicitly chosen their service area. The inventory
  // page requires this before showing units; other pages fall back to currentLocation.
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  
  // Location data with inventory mapping based on actual database
  const locations = {
    'Angola': {
      name: 'Angola',
      address: '15 Kodak Ln, Angola, IN 46703',
      inventory: [
        'Beach Oasis Combo',
        'Minnie Mouse Combo',
        'Toy Story Combo', 
        'Sunshine XL Waterslide',
        'Dino Kingdom Combo'
      ]
    },
    'Elkhart': {
      name: 'Elkhart', 
      address: '56551 Mars Dr, Elkhart, IN 46516',
      inventory: [
        'Blue Marble Combo',
        'Carnival Combo',
        'Tropical XL Dual Lane Combo',
        'Rainbow Dual Lane Combo',
        'Jelly Bean Dual Lane Combo'
      ]
    }
  };
  
  // Per-location ordering kill switch, managed from the admin dashboard.
  // Live-subscribed so pausing a location takes effect on open tabs without a
  // refresh. Defaults to "everything open" until the first snapshot arrives.
  const [orderingStatus, setOrderingStatus] = useState(() =>
    ORDERING_LOCATIONS.reduce((acc, name) => {
      acc[name] = { paused: false, message: '', updatedAt: null, updatedBy: '' };
      return acc;
    }, {})
  );

  useEffect(() => {
    const unsubscribe = subscribeToOrderingStatus(setOrderingStatus);
    return () => unsubscribe();
  }, []);

  // A confirmed area selection is only honored for 12 hours; after that the
  // customer is asked to confirm their delivery area again on their next visit.
  const SELECTION_TTL_MS = 12 * 60 * 60 * 1000;

  // Restore a previously confirmed location selection from localStorage
  useEffect(() => {
    const savedLocation = localStorage.getItem('selectedLocation');
    if (!savedLocation || !locations[savedLocation]) return;

    const savedAt = parseInt(localStorage.getItem('selectedLocationAt') || '0', 10);
    const isFresh = savedAt > 0 && Date.now() - savedAt < SELECTION_TTL_MS;

    // Keep their last area as the default for display either way, but only skip
    // the gate (confirmed) when the saved choice is still within the 12h window.
    setCurrentLocation(savedLocation);
    if (isFresh) {
      setLocationConfirmed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLocation = (locationName) => {
    if (locations[locationName]) {
      setCurrentLocation(locationName);
      setLocationConfirmed(true);
      localStorage.setItem('selectedLocation', locationName);
      localStorage.setItem('selectedLocationAt', String(Date.now()));
    }
  };
  
  const getCurrentLocationData = () => {
    return locations[currentLocation];
  };
  
  const isProductAvailable = (productName, productLocation) => {
    if (productLocation) {
      return productLocation === currentLocation || productLocation === 'Both';
    }
    
    const locationData = getCurrentLocationData();
    return locationData.inventory.some(item => 
      item.toLowerCase().includes(productName.toLowerCase()) ||
      productName.toLowerCase().includes(item.toLowerCase())
    );
  };
  
  // Ordering pause helpers. `location` accepts a service area ("Angola"),
  // a product's stored location (including "Both"), or nothing — in which case
  // the customer's currently selected area is used.
  const isOrderingPaused = (location) =>
    isOrderingPausedFor(orderingStatus, location === undefined ? currentLocation : location);

  const getOrderingPauseMessage = (location) =>
    getPauseMessageFor(orderingStatus, location === undefined ? currentLocation : location);

  const value = {
    currentLocation,
    locationConfirmed,
    changeLocation,
    locations,
    getCurrentLocationData,
    isProductAvailable,
    orderingStatus,
    isOrderingPaused,
    getOrderingPauseMessage
  };
  
  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}; 